import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRoleVerify } from "Common/RoleVerify";
import { showError, showSuccess } from "utils/notifications";
import RoleFetcher from "Common/FetchRolePermission";
import {
  getUsersForRoleAssign,
  getRolesForRoleAssign,
  getUserRoles,
  bulkAssignRoles,
} from "adminApi/RoleAssignApi/RoleAssign";
import { setMenuAccess, removeMenuAccess } from "../../../../Redux/actions/settingsActions";
import FormSelect from "Common/Custom/FormSelect";

const RoleAssignForm = ({ setRefresh, refresh }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.user);

  const canAdd = useRoleVerify("", ["can_create"]);
  const canDelete = useRoleVerify("", ["can_delete"]);
  const hasPermission = canAdd || canDelete;

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [user, setUser] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [initialRoles, setInitialRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  // ---------- Fetch helpers ----------
  const fetchUsers = async () => {
    try {
      const res = await getUsersForRoleAssign();
      if (res?.data?.success) setUsers(res.data.data);
      else showError(res?.data?.message || "Failed to fetch users");
    } catch (err) {
      console.error("Error fetching users:", err);
      showError("Error fetching users");
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await getRolesForRoleAssign();
      if (res?.data?.success) setRoles(res.data.data);
      else showError(res?.data?.message || "Failed to fetch roles");
    } catch (err) {
      console.error("Error fetching roles:", err);
      showError("Error fetching roles");
    }
  };

  const fetchUserRoles = async (id) => {
    if (!id) {
      setSelectedRoles([]);
      setInitialRoles([]);
      return;
    }
    try {
      const res = await getUserRoles({ id });
      if (res?.data?.success && Array.isArray(res.data.data)) {
        const assigned = res.data.data.map((r) => r.fk_role_id);
        setSelectedRoles(assigned);
        setInitialRoles(assigned);
      } else {
        setSelectedRoles([]);
        setInitialRoles([]);
      }
    } catch (err) {
      console.error("Error fetching user roles:", err);
      showError("Failed to load user roles");
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  useEffect(() => {
    // whenever user changes, fetch their roles (user may be number or string)
    if (user) fetchUserRoles(user);
    else {
      setSelectedRoles([]);
      setInitialRoles([]);
    }
  }, [user]);

  // Robust handler for your FormSelect which may call onChange with an event OR value
  const handleUserSelectChange = (valOrEvent) => {
    // If it's DOM event
    if (valOrEvent && valOrEvent.target && "value" in valOrEvent.target) {
      setUser(valOrEvent.target.value);
      return;
    }
    // If it's react-select (option object) or primitive value
    // Try to accept { value: ..., label: ... } OR plain value
    if (valOrEvent && typeof valOrEvent === "object" && "value" in valOrEvent) {
      setUser(valOrEvent.value);
      return;
    }

    // plain value (e.g., number / string)
    setUser(valOrEvent ?? "");
  };

  // ---------- Assign / Update roles ----------
  const handleAssignRoles = useCallback(async () => {
    if (!user) {
      showError("Please select a user");
      return;
    }

    setLoading(true);
    try {
      const added = selectedRoles.filter((r) => !initialRoles.includes(r));
      const removed = initialRoles.filter((r) => !selectedRoles.includes(r));

      if (added.length > 0 && !canAdd) {
        showError("You don’t have permission to add roles.");
        setLoading(false);
        return;
      }
      if (removed.length > 0 && !canDelete) {
        showError("You don’t have permission to remove roles.");
        setLoading(false);
        return;
      }

      const payload = {
        fk_user_id: user,
        add_roles: added,
        remove_roles: removed,
      };
      const res = await bulkAssignRoles(payload);
      if (res?.data?.success) {
        showSuccess("Roles updated successfully");
        setInitialRoles([...selectedRoles]);
        // refresh user's permissions & menu access
        setRefresh(!refresh);
        RoleFetcher({ dispatch, user: userInfo, setMenuAccess, removeMenuAccess });
      } else {
        showError(res?.data?.message || "Failed to update roles");
      }
    } catch (err) {
      console.error("Error assigning roles:", err);
      showError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  }, [user, selectedRoles, initialRoles, canAdd, canDelete]);

  if (!hasPermission) {
    return <p className="text-danger">You do not have permission to access this page.</p>;
  }

  // ---------- UI (NOT a <form> to prevent native submit/reload) ----------
  return (
    <div className="row g-4">
      {/* User dropdown (FormSelect may call onChange with event or value) */}
      <FormSelect
        field={{
          name: "user",
          label: "User Name",
          placeholder: "Select User Name",
          required: true,
          fieldSize: "col-md-12",
        }}
        value={user}
        handleChange={handleUserSelectChange}
        options={users.map((u) => ({ value: u.id, label: u.firstname }))}
        placeholder="Select User"
      />

      {/* Roles Section */}
      <div className="col-12">
        <label className="form-label text-muted">Roles *</label>
        <div className="d-flex flex-wrap">
          {roles.length > 0 ? (
            roles.map((role) => (
              <div key={role.role_id} className="form-check form-check-inline me-3 mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`role-${role.role_id}`}
                  checked={selectedRoles.includes(role.role_id)}
                  disabled={!hasPermission || loading}
                  onChange={(e) =>
                    e.target.checked
                      ? setSelectedRoles((prev) => [...prev, role.role_id])
                      : setSelectedRoles((prev) => prev.filter((id) => id !== role.role_id))
                  }
                />
                <label className="form-check-label" htmlFor={`role-${role.role_id}`}>
                  {role.role_name}
                </label>
              </div>
            ))
          ) : (
            <p className="text-muted">No roles available.</p>
          )}
        </div>
      </div>

      {/* Button (type="button" to avoid native submit) */}
      <div className="col-12 d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleAssignRoles}
          disabled={!hasPermission || loading}
        >
          {loading ? "Processing..." : initialRoles.length ? "Update" : "Assign"}
        </button>
      </div>
    </div>
  );
};

export default RoleAssignForm;
